import { supabaseAnalytics } from './supabase-analytics';

// Storage keys
const STORAGE_KEYS = {
  ANONYMOUS_ID: 'f16_anon_id',
  SOURCE: 'f16_source',
  REFERRER: 'f16_ref',
  SESSION_ID: 'f16_session_id',
  IP_ADDRESS: 'f16_ip',
} as const;

// Cached IP address (in-memory for current session)
let cachedIpAddress: string | null = null;

/**
 * Fetch IP address from ipify and cache it
 */
async function fetchIpAddress(): Promise<string | null> {
  // Return cached value if available
  if (cachedIpAddress) {
    return cachedIpAddress;
  }

  // Check sessionStorage first
  const storedIp = sessionStorage.getItem(STORAGE_KEYS.IP_ADDRESS);
  if (storedIp) {
    cachedIpAddress = storedIp;
    return storedIp;
  }

  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (response.ok) {
      const data = await response.json();
      cachedIpAddress = data.ip;
      sessionStorage.setItem(STORAGE_KEYS.IP_ADDRESS, data.ip);
      return data.ip;
    }
  } catch (err) {
    console.error('[Analytics] Failed to fetch IP:', err);
  }

  return null;
}

/**
 * Get cached IP address (non-blocking)
 */
export function getIpAddress(): string | null {
  return cachedIpAddress || sessionStorage.getItem(STORAGE_KEYS.IP_ADDRESS);
}

// URL parameters / cookie names to capture
const TRACKING_KEYS = ['f16_anon_id', 'f16_source', 'f16_ref'] as const;

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Capture anonymous ID and tracking params from cookies/URL and store in localStorage
 * Priority: URL params > Cookies > Existing localStorage
 */
export function captureAnonymousId(): void {
  const url = new URL(window.location.href);
  let hasUrlParams = false;

  for (const key of TRACKING_KEYS) {
    // Skip if already in localStorage
    if (localStorage.getItem(key)) {
      continue;
    }

    // 1. Try URL params first (highest priority)
    const urlValue = url.searchParams.get(key);
    if (urlValue) {
      localStorage.setItem(key, urlValue);
      hasUrlParams = true;
      continue;
    }

    // 2. Try cookies (set by landing page)
    const cookieValue = getCookie(key);
    if (cookieValue) {
      localStorage.setItem(key, cookieValue);
    }
  }

  // Clean URL by removing tracking params
  if (hasUrlParams) {
    for (const key of TRACKING_KEYS) {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Get stored anonymous ID
 */
export function getAnonymousId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ANONYMOUS_ID);
}

/**
 * Get stored source
 */
export function getSource(): string | null {
  return localStorage.getItem(STORAGE_KEYS.SOURCE);
}

/**
 * Get stored referrer
 */
export function getReferrer(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFERRER);
}

/**
 * Clear anonymous ID and related tracking data after identity link
 */
export function clearAnonymousId(): void {
  localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_ID);
  localStorage.removeItem(STORAGE_KEYS.SOURCE);
  localStorage.removeItem(STORAGE_KEYS.REFERRER);
}

/**
 * Get or create session ID (persists for browser session)
 */
export function getSessionId(): string {
  let sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
}

/**
 * Track event parameters
 */
interface TrackEventParams {
  user_id?: string;
  element_id?: string;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Track an event to the product_events table
 */
export async function trackEvent(
  eventCategory: string,
  eventName: string,
  params: TrackEventParams = {}
): Promise<void> {
  const anonymousId = getAnonymousId();
  const userId = params.user_id;

  // Track if we have either anonymous ID OR user ID
  if (!anonymousId && !userId) {
    return;
  }

  // Get IP address (try cached first, fetch if not available)
  let ipAddress = getIpAddress();
  if (!ipAddress) {
    ipAddress = await fetchIpAddress();
  }

  const eventData = {
    anonymous_id: anonymousId || null,
    user_id: params.user_id || null,
    session_id: getSessionId(),
    event_category: eventCategory,
    event_name: eventName,
    page_path: window.location.pathname,
    element_id: params.element_id,
    duration_ms: params.duration_ms,
    metadata: {
      referrer: getReferrer() || document.referrer,
      source: getSource(),
      user_agent: navigator.userAgent,
      ip_address: ipAddress,
      ...params.metadata,
    },
  };

  try {
    const { error } = await supabaseAnalytics
      .from('product_events')
      .insert(eventData);

    if (error) {
      console.error('[Analytics] Failed to track event:', error);
    }
  } catch (err) {
    console.error('[Analytics] Error tracking event:', err);
  }
}

/**
 * Link anonymous identity to authenticated user
 */
export async function linkIdentity(userId: string): Promise<boolean> {
  const anonymousId = getAnonymousId();

  if (!anonymousId) {
    return false;
  }

  try {
    const { error } = await supabaseAnalytics.rpc('link_anonymous_to_user', {
      p_anonymous_id: anonymousId,
      p_user_id: userId,
    });

    if (error) {
      console.error('[Analytics] Failed to link identity:', error);
      return false;
    }

    // Clear anonymous ID after successful link
    clearAnonymousId();
    return true;
  } catch (err) {
    console.error('[Analytics] Error linking identity:', err);
    return false;
  }
}

/**
 * Track button click event
 */
export function trackButtonClick(
  buttonId: string,
  buttonText?: string,
  metadata?: Record<string, unknown>
): void {
  trackEvent('ui_interaction', 'button_click', {
    element_id: buttonId,
    metadata: {
      button_text: buttonText,
      ...metadata,
    },
  });
}

/**
 * Duration milestones to track (in milliseconds)
 */
const DURATION_MILESTONES = [5000, 10000, 30000, 60000, 120000, 300000] as const;

/**
 * Page duration tracker instance
 */
let durationTracker: PageDurationTracker | null = null;

/**
 * Page duration tracker class
 * Tracks time spent on page and fires events at milestones
 */
class PageDurationTracker {
  private startTime: number;
  private currentPath: string;
  private trackedMilestones: Set<number> = new Set();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isActive = true;

  constructor() {
    this.startTime = Date.now();
    this.currentPath = window.location.pathname;
    this.startTracking();
    this.setupVisibilityHandler();
  }

  private startTracking(): void {
    // Check milestones every second
    this.intervalId = setInterval(() => {
      if (!this.isActive) return;

      const elapsed = Date.now() - this.startTime;

      for (const milestone of DURATION_MILESTONES) {
        if (elapsed >= milestone && !this.trackedMilestones.has(milestone)) {
          this.trackedMilestones.add(milestone);
          trackEvent('engagement', 'page_duration', {
            duration_ms: milestone,
            metadata: {
              milestone_seconds: milestone / 1000,
              page_path: this.currentPath,
            },
          });
        }
      }
    }, 1000);
  }

  private setupVisibilityHandler(): void {
    document.addEventListener('visibilitychange', () => {
      this.isActive = document.visibilityState === 'visible';
    });
  }

  /**
   * Reset tracker for new page
   */
  reset(newPath?: string): void {
    this.startTime = Date.now();
    this.currentPath = newPath || window.location.pathname;
    this.trackedMilestones.clear();
  }

  /**
   * Stop tracking
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Get current elapsed time
   */
  getElapsedTime(): number {
    return Date.now() - this.startTime;
  }
}

/**
 * Start page duration tracking
 */
export function startDurationTracking(): void {
  if (!getAnonymousId()) return;

  if (durationTracker) {
    durationTracker.stop();
  }
  durationTracker = new PageDurationTracker();
}

/**
 * Reset duration tracking (call on route change)
 */
export function resetDurationTracking(newPath?: string): void {
  if (durationTracker) {
    durationTracker.reset(newPath);
  }
}

/**
 * Stop duration tracking
 */
export function stopDurationTracking(): void {
  if (durationTracker) {
    durationTracker.stop();
    durationTracker = null;
  }
}

/**
 * Initialize analytics on app load
 */
export async function initializeAnalytics(): Promise<void> {
  // Capture anonymous ID from URL params / cookies
  captureAnonymousId();

  // Track page view if we have an anonymous ID
  const anonymousId = getAnonymousId();
  if (anonymousId) {
    // Prefetch IP address for faster subsequent events
    await fetchIpAddress();

    trackEvent('page_view', 'app_loaded', {
      metadata: {
        landing_source: getSource(),
        landing_referrer: getReferrer(),
      },
    });

    // Start duration tracking
    startDurationTracking();
  }
}
